
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Divider,
  Paper,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
} from "@material-ui/core";
import { Add, Remove, ShoppingCart, Delete } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../services/api";
import DialogActions from '@material-ui/core/DialogActions';

// Adiciona animação de hover nos cards de produto (apenas uma vez)
if (typeof window !== "undefined" && typeof document !== "undefined" && !document.getElementById('produto-card-hover-style')) {
  const style = document.createElement('style');
  style.id = 'produto-card-hover-style';
  style.innerHTML = `
    .produto-card-hover:hover > .MuiPaper-root {
      box-shadow: 0 8px 32px rgba(30,163,240,0.18) !important;
      transform: translateY(-4px) scale(1.025);
      transition: box-shadow 0.25s, transform 0.25s;
    }
  `;
  document.head.appendChild(style);
}

export default function SelfCheckout() {
  const history = useHistory();
  const [produtos, setProdutos] = useState([]);
  const [itens, setItens] = useState([]);
  const [nomeCliente, setNomeCliente] = useState("");
  const [telefoneCliente, setTelefoneCliente] = useState("55");
  const [loading, setLoading] = useState(false);
  const [cardapioDia, setCardapioDia] = useState(null);
  const [openCardapio, setOpenCardapio] = useState(false);
  const [tipoPedido, setTipoPedido] = useState("retirada");
  const [endereco, setEndereco] = useState({ rua: "", numero: "", bairro: "" });
  const [pagamento, setPagamento] = useState({ forma: "", troco: "" });
  const [taxaEntrega, setTaxaEntrega] = useState(0);
  const [opcaoCardapio, setOpcaoCardapio] = useState("1");
  const [novoItem, setNovoItem] = useState({ nome: "", quantidade: 1, preco: "", observacoes: "" });
  const [modalItem, setModalItem] = useState(null); // {produto, quantidade, preco, opcaoCardapio, observacoes}
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [pedidoBarOpen, setPedidoBarOpen] = useState(false);
  // Para remover unidades de um item
  const [itemParaRemover, setItemParaRemover] = useState(null); // {item, idx}
  const [qtdRemover, setQtdRemover] = useState(1);

  // Refs para scroll automático no mobile
  const trocoRef = useRef(null);
  const enderecoRef = useRef(null);

  // Scrolla para o campo de troco quando seleciona Dinheiro
  useEffect(() => {
    if (isMobile && pedidoBarOpen && pagamento.forma === 'Dinheiro' && trocoRef.current) {
      trocoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [pagamento.forma, isMobile, pedidoBarOpen]);

  // Scrolla para o campo de endereço quando seleciona Entrega
  useEffect(() => {
    if (isMobile && pedidoBarOpen && tipoPedido === 'entrega' && enderecoRef.current) {
      enderecoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [tipoPedido, isMobile, pedidoBarOpen]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/produtos")
      .then((res) => {
        let data = res.data?.data || [];
        if (!Array.isArray(data)) data = [data];
        setProdutos(data);
      })
      .catch(() => toast.error("Erro ao carregar produtos"))
      .finally(() => setLoading(false));
  }, []);

  // Atualiza taxa de entrega
  // Garante que o telefone sempre começa com 55
  // Máscara visual para telefone: +55 (XX) XXXXX-XXXX
  const formatTelefone = (value) => {
    // Garante que value é só números e começa com 55
    let v = value.replace(/\D/g, "");
    if (!v.startsWith("55")) v = "55" + v.replace(/^55/, "");
    v = v.slice(0, 13); // 55 + 11 dígitos
    // Monta máscara
    const ddd = v.slice(2, 4);
    const parte1 = v.slice(4, 9);
    const parte2 = v.slice(9, 13);
    let mask = "+55";
    if (ddd) mask += ` (${ddd}`;
    if (ddd && ddd.length === 2) mask += ")";
    if (parte1) mask += ` ${parte1}`;
    if (parte2) mask += `-${parte2}`;
    return mask;
  };

  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (!value.startsWith("55")) {
      value = "55" + value.replace(/^55/, "");
    }
    if (value.length > 13) value = value.slice(0, 13);
    setTelefoneCliente(value);
  };
  useEffect(() => {
    if (tipoPedido !== "entrega") {
      setTaxaEntrega(0);
      return;
    }
    const bairro = (endereco.bairro || "").trim().toLowerCase();
    if (!bairro) return setTaxaEntrega(0);
    setTaxaEntrega(bairro.includes("centro") ? 1 : 2);
  }, [tipoPedido, endereco.bairro]);

  // Busca cardápio do dia
  const buscarCardapioDia = async () => {
    try {
      const dias = [
        "Domingo",
        "Segunda",
        "Terça",
        "Quarta",
        "Quinta",
        "Sexta",
        "Sábado",
      ];
      const hoje = new Date();
      const diaLabel = dias[hoje.getDay()] || undefined;
      if (!diaLabel) return;
      const res = await api.get("/cardapio", { params: { dia: diaLabel } });
      setCardapioDia(res.data?.data?.[0] || null);
      setOpenCardapio(true);
    } catch {
      toast.error("Erro ao buscar cardápio do dia");
    }
  };

  // Opções válidas do cardápio do dia
  const opcoesCardapioValidas = [cardapioDia?.opcao1_nome, cardapioDia?.opcao2_nome].filter(Boolean);

  // Atualiza campos ao selecionar produto
  const handleSelectProduto = (produto) => {
    if (!produto) return setNovoItem({ nome: "", quantidade: 1, preco: "", observacoes: "" });
    const preco = Number(produto.valor) || Number(produto.preco) || 0;
    const nome = produto.nome.toLowerCase();
    const isMarmita = nome.includes("marmita") || nome.includes("quentinha");
    setNovoItem({
      nome: produto.nome,
      preco: preco.toFixed(2),
      quantidade: 1,
      observacoes: !isMarmita && produto.descricao ? produto.descricao : "",
    });
    // Se for marmita, força mostrar opções
    if (isMarmita && opcoesCardapioValidas.length > 0) setOpcaoCardapio("1");
  };

  // Adiciona item ao carrinho
  const handleAddNovoItem = () => {
    if (!novoItem.nome || !novoItem.preco) {
      toast.error("Preencha nome e preço do item");
      return;
    }
    // Se for produto do cardápio do dia, exige opção
    const isMarmita = novoItem.nome.toLowerCase().includes("marmita") || novoItem.nome.toLowerCase().includes("quentinha");
    if (isMarmita && opcoesCardapioValidas.length > 1 && !opcaoCardapio) {
      toast.error("Selecione a opção do cardápio");
      return;
    }
    setItens((prev) => [
      ...prev,
      {
        ...novoItem,
        opcaoCardapio: isMarmita ? opcaoCardapio : undefined,
      },
    ]);
    setNovoItem({ nome: "", quantidade: 1, preco: "", observacoes: "" });
    setOpcaoCardapio("1");
  };

  // Abre modal ao clicar em adicionar
  const handleAddItemClick = async (produto) => {
    const nome = (produto.nome || '').toLowerCase();
    const isMarmita = nome.includes('marmita') || nome.includes('quentinha');
    let opcaoCardapio = '';
    // Se for marmita e não tem cardápio carregado, busca antes de abrir o modal
    let cardapio = cardapioDia;
    if (isMarmita && !cardapioDia) {
      try {
        const dias = ["Domingo","Segunda","Terça","Quarta","Quinta","Sexta","Sábado"];
        const hoje = new Date();
        const diaLabel = dias[hoje.getDay()] || undefined;
        if (diaLabel) {
          const res = await api.get("/cardapio", { params: { dia: diaLabel } });
          cardapio = res.data?.data?.[0] || null;
          setCardapioDia(cardapio);
        }
      } catch {}
    }
    const opcoesValidas = [cardapio?.opcao1_nome, cardapio?.opcao2_nome].filter(Boolean);
    if (isMarmita && opcoesValidas.length === 1) {
      opcaoCardapio = cardapio?.opcao1_nome ? '1' : '2';
    }
    setModalItem({
      ...produto,
      quantidade: 1,
      preco: Number(produto.valor || produto.preco).toFixed(2),
      opcaoCardapio,
      observacoes: '',
    });
  };

  // Confirma adição do item do modal
  const handleConfirmAddItem = () => {
    if (!modalItem) return;
    // Se for marmita, exige opção
    const isMarmita = modalItem.nome.toLowerCase().includes('marmita') || modalItem.nome.toLowerCase().includes('quentinha');
    if (isMarmita && opcoesCardapioValidas.length > 1 && !modalItem.opcaoCardapio) {
      toast.error('Selecione a opção do cardápio');
      return;
    }
    setItens((prev) => [
      ...prev,
      {
        ...modalItem,
        opcaoCardapio: isMarmita ? modalItem.opcaoCardapio : undefined,
      },
    ]);
    setModalItem(null);
  };

  const handleRemoveItem = (produto) => {
    setItens((prev) => {
      const idx = prev.findIndex((i) => i.id === produto.id);
      if (idx >= 0) {
        const novo = [...prev];
        if (novo[idx].quantidade > 1) novo[idx].quantidade -= 1;
        else novo.splice(idx, 1);
        return novo;
      }
      return prev;
    });
  };

  const total = itens.reduce(
    (acc, i) => acc + Number(i.valor || i.preco || 0) * i.quantidade,
    0
  );
  const totalComTaxa = total + taxaEntrega;

  // 📨 Monta resumo igual ao Carrinho.js
  const resumo = `
📦 *Resumo do pedido*  

${itens
    .map((i) => {
      const nomeOriginal = String(i.nome || "").trim();
      const nome = nomeOriginal
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      let icone = "🍽️";
      if (nome.includes("marmita") || nome.includes("quentinha")) icone = "🍽️";
      else if (nome.includes("coca") || nome.includes("suco") || nome.includes("fanta") || nome.includes("agua")) icone = "🥤";
      else if (nome.includes("pudim") || nome.includes("bolo") || nome.includes("doce")) icone = "🍰";
      else if (nome.includes("lanche") || nome.includes("hamb") || nome.includes("xis")) icone = "🍔";
      else if (nome.includes("pizza")) icone = "🍕";
      const linhaOpcao = i.opcaoCardapio ? `Opção ${i.opcaoCardapio}  \n` : "";
      return `${icone} *${nomeOriginal}*  \n${linhaOpcao}Quantidade: ${i.quantidade}  \nPreço: R$ ${Number(i.preco || i.valor).toFixed(2)}  \nObs: ${i.observacoes || "Nenhuma"}`;
    })
    .join("\n\n")}

${tipoPedido === "entrega" ? `🚚 *Taxa de entrega:* R$ ${taxaEntrega.toFixed(2)}` : ""}
💰 *Total:* R$ ${Number(totalComTaxa).toFixed(2)}

${tipoPedido === "entrega"
    ? `🏠 Endereço: ${endereco.rua}, ${endereco.numero}, ${endereco.bairro}`
    : ""}
💳 *Forma de Pagamento:* ${pagamento.forma}
${pagamento.forma === "Dinheiro" && pagamento.troco ? `\nTroco para: R$ ${pagamento.troco}` : ""}

👤 Nome: ${nomeCliente}\n📱 Telefone: ${telefoneCliente}

✅ *Confirma o pedido?*
`.trim();

  const handleEnviarPedido = async () => {
    if (itens.length === 0) return toast.warn("Carrinho vazio!");
    if (!nomeCliente.trim()) return toast.error("Nome é obrigatório!");
    if (!telefoneCliente.trim()) return toast.error("Telefone é obrigatório!");
    if (tipoPedido === "entrega" && (!endereco.rua || !endereco.numero || !endereco.bairro)) {
      return toast.error("Preencha o endereço completo!");
    }
    if (!pagamento.forma) return toast.error("Selecione a forma de pagamento!");
    try {
      // Cria um novo chat
      const chatRes = await api.post("/chats/findOrCreate", {
        telefone: telefoneCliente,
        nome: nomeCliente,
        ultimoTexto: "Pedido realizado pelo sistema",
        forceNew: true,
      });
      const chatId = chatRes.data?.id;
      if (!chatId) throw new Error("Não foi possível criar o chat");
      await api.post("/pedidos", {
        chatId,
        itens,
        total: totalComTaxa,
        tipo: tipoPedido,
        endereco: tipoPedido === "entrega" ? endereco : null,
        pagamento: {
          ...pagamento,
          taxaEntrega,
        },
        selfcheckout: true,
      });
      toast.success("Pedido enviado! Aguarde confirmação.");
      setItens([]);
      setNomeCliente("");
      setTelefoneCliente("");
      setEndereco({ rua: "", numero: "", bairro: "" });
      setPagamento({ forma: "", troco: "" });
      setTipoPedido("retirada");
      history.push("/pedidoEfetuado");
    } catch (err) {
      toast.error("Erro ao enviar pedido.");
    }
  };

  // Agrupa produtos por categoria (usando 'observacoes' como categoria)
  const produtosPorCategoria = produtos.reduce((acc, prod) => {
    const cat = prod.observacoes || "Outros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(prod);
    return acc;
  }, {});

  return (
    <React.Fragment>
      {/* Header com logo, sem botões */}
      <Header showButtons={false} />
      <Box
        minHeight="110vh"
        bgcolor="#f3f6fa"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        p={isMobile ? 1 : 4}
        style={{ paddingTop: 80, paddingBottom: isMobile ? 80 : 0 }} // espaço para o header fixo e para o downbar
      >
      <Grid container spacing={isMobile ? 1 : 4} style={{ maxWidth: 1200, width: '100%' }}>
        <Grid item xs={12} md={8}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={isMobile ? 3 : 5} mt={isMobile ? 2 : 4}>
            <Typography variant="h5" style={{ fontWeight: 700 }}>
              🛒 Escolha seus produtos
            </Typography>
            <Button
              variant="contained"
              onClick={buscarCardapioDia}
              style={{ fontWeight: 600, background: '#1ea3f0ff', color: '#fff' }}
              // Removido color="secondary" para evitar sobrescrever
            >
              Ver Cardápio do Dia
            </Button>
          </Box>
          {/* Exibe produtos divididos por categoria em ordem fixa */}
          {(() => {
            // Ordem fixa desejada
            const ordemDesejada = ['marmita', 'quentinha', 'bebida', 'doce'];
            // Função para determinar prioridade
            const prioridade = (cat) => {
              const nome = cat.toLowerCase();
              if (nome.includes('marmita') || nome.includes('quentinha')) return 0;
              if (nome.includes('bebida')) return 1;
              if (nome.includes('doce')) return 2;
              return 3;
            };
            // Ordena as categorias
            const categoriasOrdenadas = Object.entries(produtosPorCategoria).sort((a, b) => {
              const pA = prioridade(a[0]);
              const pB = prioridade(b[0]);
              if (pA !== pB) return pA - pB;
              // Se mesma prioridade, ordem alfabética
              return a[0].localeCompare(b[0]);
            });
            return categoriasOrdenadas.map(([categoria, lista]) => (
            <Box key={categoria} mb={isMobile ? 2 : 3}>
              <Box display="flex" alignItems="center" style={{ margin: '16px 0 8px 0' }}>
                <span style={{
                  display: 'inline-block',
                  width: 6,
                  height: 32,
                  borderRadius: 4,
                  background: '#1ea3f0',
                  marginRight: 12,
                }} />
                <Typography
                  variant="h6"
                  style={{
                    color: '#2563eb',
                    fontWeight: 700,
                    fontSize: 22,
                    letterSpacing: 0.5,
                    textShadow: '0 2px 8px #1ea3f022',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {(() => {
                    const nome = categoria.toLowerCase();
                    if (nome.includes('marmita')) return '🍽️ ' + categoria;
                    if (nome.includes('bebida')) return '🥤 ' + categoria;
                    if (nome.includes('doce')) return '🍰 ' + categoria;
                    if (nome.includes('lanche') || nome.includes('hamb') || nome.includes('xis')) return '🍔 ' + categoria;
                    if (nome.includes('pizza')) return '🍕 ' + categoria;
                    return categoria;
                  })()}
                </Typography>
              </Box>
              <Grid container spacing={isMobile ? 1 : 2}>
                {lista.map((produto, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={produto.id}>
                    <div className="produto-card-hover" style={{ borderRadius: 14, minHeight: 210 }}>
                      <Card style={{ borderRadius: 14, minHeight: 210, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.10)', border: '1.5px solid #1ea3f0', transition: 'box-shadow 0.22s, border-color 0.22s, transform 0.22s' }}>
                        <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 8 }}>
                          <img
                            src={produto.fotoUrl || 'https://via.placeholder.com/90x90?text=Foto'}
                            alt={produto.nome}
                            style={{
                              width: 90,
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: '50%',
                              background: '#f3f4f6',
                              border: '2px solid #e5e7eb',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                              marginBottom: 10,
                              display: 'block',
                            }}
                          />
                          <Typography variant="h6" align="center">{produto.nome}</Typography>
                          <Typography color="textSecondary" align="center">
                            R$ {Number(produto.valor || produto.preco).toFixed(2)}
                          </Typography>
                          {produto.descricao && (
                            <Typography variant="body2" style={{ marginTop: 8 }} align="center">
                              {produto.descricao}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<Add />}
                            onClick={() => handleAddItemClick(produto)}
                          >
                            Adicionar
                          </Button>
                          <Button
                            size="small"
                            color="secondary"
                            startIcon={<Remove />}
                            onClick={() => handleRemoveItem(produto)}
                            disabled={
                              !itens.find((i) => i.id === produto.id)
                            }
                          >
                            Remover
                          </Button>
                        </CardActions>
                      </Card>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Box>
            ));
          })()}
        </Grid>
        {/* Desktop: mostra o pedido na lateral. Mobile: mostra na bottom bar */}
        {!isMobile && (
          <Grid item xs={12} md={4}>
            <Paper
              style={{
                padding: 28,
                minHeight: 420,
                borderRadius: 18,
                background: '#fff',
                border: '2.5px solid #14b8a6',
                boxShadow: '0 6px 10px #14b8a6',
                position: 'relative',
              }}
            >
              {/* ...existing code... (Resumo do pedido igual estava antes) */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: 800,
                  color: '#14b8a6',
                  letterSpacing: 1,
                  fontSize: 22,
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ShoppingCart style={{ verticalAlign: "middle", marginRight: 8, fontSize: 28, color: '#14b8a6' }} />
                Seu Pedido
              </Typography>
              <Divider style={{ margin: "12px 0" }} />
              {itens.length === 0 ? (
                <Typography color="textSecondary">Nenhum produto selecionado.</Typography>
              ) : (
                <>
                  {itens.map((item, idx) => (
                    <Box
                      key={item.id + (item.opcaoCardapio || '') + (item.observacoes || '')}
                      display="flex"
                      flexDirection="column"
                      mb={1.5}
                      p={2}
                      borderRadius={12}
                      bgcolor="#fff"
                      style={{
                        border: '1.5px solid #90e0ff',
                        boxShadow: '0 2px 12px #1ea3f022',
                        minHeight: 54,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography>
                          {item.quantidade}x {item.nome}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography>
                            R$ {(Number(item.valor || item.preco) * item.quantidade).toFixed(2)}
                          </Typography>
                          <IconButton
                            size="small"
                            color="secondary"
                            aria-label="Remover item"
                            onClick={() => {
                              if (item.quantidade > 1) {
                                setItemParaRemover({ item, idx });
                                setQtdRemover(1);
                              } else {
                                setItens(prev => {
                                  const novo = [...prev];
                                  novo.splice(idx, 1);
                                  return novo;
                                });
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                            {/* Dialog para remover unidades de um item do pedido */}
                            <Dialog open={!!itemParaRemover} onClose={() => setItemParaRemover(null)} maxWidth="xs" fullWidth>
                              <DialogTitle>Remover produto</DialogTitle>
                              <DialogContent>
                                {itemParaRemover && (
                                  <>
                                    <Typography gutterBottom>
                                      Quantas unidades de <b>{itemParaRemover.item.nome}</b> deseja remover?
                                    </Typography>
                                    <TextField
                                      type="number"
                                      inputProps={{ min: 1, max: itemParaRemover.item.quantidade }}
                                      value={qtdRemover}
                                      onChange={e => {
                                        let v = Number(e.target.value);
                                        if (v < 1) v = 1;
                                        if (v > itemParaRemover.item.quantidade) v = itemParaRemover.item.quantidade;
                                        setQtdRemover(v);
                                      }}
                                      label="Quantidade"
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      style={{ marginTop: 8, marginBottom: 8 }}
                                    />
                                  </>
                                )}
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={() => setItemParaRemover(null)} color="secondary">Cancelar</Button>
                                <Button
                                  onClick={() => {
                                    if (!itemParaRemover) return;
                                    setItens(prev => {
                                      const novo = [...prev];
                                      const idx = itemParaRemover.idx;
                                      if (idx < 0 || idx >= novo.length) return prev;
                                      if (novo[idx].quantidade > qtdRemover) {
                                        novo[idx] = { ...novo[idx], quantidade: novo[idx].quantidade - qtdRemover };
                                      } else {
                                        novo.splice(idx, 1);
                                      }
                                      return novo;
                                    });
                                    setItemParaRemover(null);
                                  }}
                                  color="primary"
                                  variant="contained"
                                >
                                  Remover
                                </Button>
                              </DialogActions>
                            </Dialog>
                      {item.opcaoCardapio && (
                        <Typography variant="body2" color="primary">Opção {item.opcaoCardapio}</Typography>
                      )}
                      {item.observacoes && (
                        <Typography variant="body2" color="textSecondary">Obs: {item.observacoes}</Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
              <Divider style={{ margin: "12px 0" }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Total: R$ {totalComTaxa.toFixed(2)}{' '}
                {tipoPedido === 'entrega' && taxaEntrega > 0 && (
                  <span style={{ fontWeight: 400, fontSize: 13, color: '#555' }}>
                    (inclui R$ {taxaEntrega.toFixed(2)} de entrega)
                  </span>
                )}
              </Typography>
              <Box mt={2}>
                <TextField
                  label="Nome"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  value={nomeCliente}
                  onChange={(e) => setNomeCliente(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
                <TextField
                  label="Telefone"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  value={formatTelefone(telefoneCliente)}
                  onChange={handleTelefoneChange}
                  inputProps={{ maxLength: 19 }}
                  style={{ marginBottom: 18 }}
                />
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Tipo de Pedido
                </Typography>
                <RadioGroup row value={tipoPedido} onChange={e => setTipoPedido(e.target.value)} style={{ marginBottom: 10 }}>
                  <FormControlLabel value="entrega" control={<Radio color="primary" />} label="Entrega" />
                  <FormControlLabel value="retirada" control={<Radio color="primary" />} label="Retirada" />
                </RadioGroup>
                {tipoPedido === 'entrega' && (
                  <Box mb={1}>
                    <TextField
                      label="Rua"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={endereco.rua}
                      onChange={e => setEndereco({ ...endereco, rua: e.target.value })}
                      style={{ marginBottom: 8 }}
                    />
                    <TextField
                      label="Número"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={endereco.numero}
                      onChange={e => setEndereco({ ...endereco, numero: e.target.value })}
                      style={{ marginBottom: 8 }}
                    />
                    <TextField
                      label="Bairro"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={endereco.bairro}
                      onChange={e => setEndereco({ ...endereco, bairro: e.target.value })}
                    />
                    {endereco.bairro && (
                      <Typography variant="body2" style={{ color: '#444', marginTop: 6, fontWeight: 500 }}>
                        💸 Taxa de entrega: R$ {taxaEntrega.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                )}
                <Divider style={{ margin: '16px 0 10px 0' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Forma de Pagamento
                </Typography>
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={pagamento.forma}
                  onChange={e => setPagamento({ ...pagamento, forma: e.target.value })}
                  style={{ marginBottom: 10 }}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                  <MenuItem value="Cartão">Cartão</MenuItem>
                  <MenuItem value="PIX">PIX</MenuItem>
                </TextField>
                {pagamento.forma === 'Dinheiro' && (
                  <TextField
                    label="Troco para quanto?"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={pagamento.troco}
                    onChange={e => setPagamento({ ...pagamento, troco: e.target.value })}
                    style={{ marginBottom: 10 }}
                  />
                )}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  style={{ marginTop: 16, fontWeight: 700, borderRadius: 8, boxShadow: '0 2px 8px #2563eb33', backgroundColor: '#14b8a6' }}
                  onClick={handleEnviarPedido}
                >
                  Enviar Pedido
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* Bottom bar para mobile */}
      {isMobile && (
        <Box
          position="fixed"
          left={0}
          right={0}
          bottom={0}
          zIndex={1300}
          style={{
            background: '#fff',
            borderTopLeftRadius: pedidoBarOpen ? 18 : 18,
            borderTopRightRadius: pedidoBarOpen ? 18 : 18,
            boxShadow: '0 -2px 16px #0002',
            transition: 'height 0.3s',
            height: pedidoBarOpen ? '62vh' : 46,
            minHeight: pedidoBarOpen ? 170 : 46,
            maxHeight: '85vh',
            overflow: 'hidden',
            cursor: 'pointer',
            paddingBottom: pedidoBarOpen ? 36 : 0, // padding extra menor para não cortar botão
          }}
        >
          {/* Barra de puxar */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            onClick={() => setPedidoBarOpen((v) => !v)}
            style={{ paddingTop: 8, paddingBottom: 8, userSelect: 'none' }}
          >
            <Box
              width={40}
              height={5}
              borderRadius={3}
              bgcolor="#ccc"
              mb={pedidoBarOpen ? 1 : 0}
              style={{ transition: 'margin 0.2s' }}
            />
            {!pedidoBarOpen && (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <ShoppingCart style={{ marginRight: 8, color: '#14b8a6' }} />
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#14b8a6' }}>
                  Seu Pedido ({itens.length})
                </Typography>
                <Typography variant="subtitle2" style={{ marginLeft: 12, color: '#14b8a6' }}>
                  R$ {totalComTaxa.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>
          {/* Conteúdo do pedido (expande) */}
          {pedidoBarOpen && (
            <Box position="relative" display="flex" flexDirection="column" height="100%">
              <Box px={1} pt={1} pb={0} flex={1} style={{ overflowY: 'auto', minHeight: 0, height: '100%' }}>
                <Typography variant="h6" style={{ fontWeight: 700, color: '#14b8a6', fontSize: 18 }}>
                  <ShoppingCart style={{ verticalAlign: "middle", color: '#14b8a6', fontSize: 20 }} /> Seu Pedido
                </Typography>
                <Divider style={{ margin: "8px 0" }} />
                {itens.length === 0 ? (
                  <Typography color="textSecondary" style={{ fontSize: 14 }}>Nenhum produto selecionado.</Typography>
                ) : (
                  <>
                    {itens.map((item, idx) => (
                      <React.Fragment key={item.id + (item.opcaoCardapio || '') + (item.observacoes || '')}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          mb={1}
                          p={1.2}
                          borderRadius={10}
                          bgcolor="#fff"
                          style={{
                            border: '1px solid #14b8a6',
                            boxShadow: '0 1px 6px #14b8a622',
                            minHeight: 38,
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography style={{ fontSize: 15 }}>
                              {item.quantidade}x {item.nome}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography style={{ color: '#14b8a6', fontWeight: 700, fontSize: 15 }}>
                                R$ {(Number(item.valor || item.preco) * item.quantidade).toFixed(2)}
                              </Typography>
                              <IconButton
                                size="small"
                                color="secondary"
                                aria-label="Remover item"
                                onClick={() => {
                                  if (item.quantidade > 1) {
                                    setItemParaRemover({ item, idx });
                                    setQtdRemover(1);
                                  } else {
                                    setItens(prev => {
                                      const novo = [...prev];
                                      novo.splice(idx, 1);
                                      return novo;
                                    });
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          {item.opcaoCardapio && (
                            <Typography variant="body2" color="primary" style={{ fontSize: 13 }}>Opção {item.opcaoCardapio}</Typography>
                          )}
                          {item.observacoes && (
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>Obs: {item.observacoes}</Typography>
                          )}
                        </Box>
                        {/* Espaço extra após o último produto, como separação de categoria, mas um pouco menor */}
                        {idx === itens.length - 1 && (
                          <Box height={18} />
                        )}
                      </React.Fragment>
                    ))}
                  </>
                )}
                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="subtitle1" style={{ fontWeight: 600, fontSize: 16 }}>
                  Total: R$ {totalComTaxa.toFixed(2)}{' '}
                  {tipoPedido === 'entrega' && taxaEntrega > 0 && (
                    <span style={{ fontWeight: 400, fontSize: 12, color: '#555' }}>
                      (inclui R$ {taxaEntrega.toFixed(2)} de entrega)
                    </span>
                  )}
                </Typography>
                <Box mt={1.2}>
                  <TextField
                    label="Nome"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={nomeCliente}
                    onChange={(e) => setNomeCliente(e.target.value)}
                    style={{ marginBottom: 7 }}
                  />
                  <TextField
                    label="Telefone"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={formatTelefone(telefoneCliente)}
                    onChange={handleTelefoneChange}
                    inputProps={{ maxLength: 19 }}
                    style={{ marginBottom: 10 }}
                  />
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 2, fontSize: 14 }}>
                    Tipo de Pedido
                  </Typography>
                  <RadioGroup row value={tipoPedido} onChange={e => setTipoPedido(e.target.value)} style={{ marginBottom: 7 }}>
                    <FormControlLabel value="entrega" control={<Radio color="primary" />} label="Entrega" />
                    <FormControlLabel value="retirada" control={<Radio color="primary" />} label="Retirada" />
                  </RadioGroup>
                  {tipoPedido === 'entrega' && (
                    <Box mb={1} ref={enderecoRef}>
                      <TextField
                        label="Rua"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={endereco.rua}
                        onChange={e => setEndereco({ ...endereco, rua: e.target.value })}
                        style={{ marginBottom: 6 }}
                      />
                      <TextField
                        label="Número"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={endereco.numero}
                        onChange={e => setEndereco({ ...endereco, numero: e.target.value })}
                        style={{ marginBottom: 6 }}
                      />
                      <TextField
                        label="Bairro"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={endereco.bairro}
                        onChange={e => setEndereco({ ...endereco, bairro: e.target.value })}
                      />
                      {endereco.bairro && (
                        <Typography variant="body2" style={{ color: '#444', marginTop: 4, fontWeight: 500, fontSize: 13 }}>
                          💸 Taxa de entrega: R$ {taxaEntrega.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Divider style={{ margin: '10px 0 7px 0' }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 2, fontSize: 14 }}>
                    Forma de Pagamento
                  </Typography>
                  <TextField
                    select
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={pagamento.forma}
                    onChange={e => setPagamento({ ...pagamento, forma: e.target.value })}
                    style={{ marginBottom: 7 }}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                    <MenuItem value="Cartão">Cartão</MenuItem>
                    <MenuItem value="PIX">PIX</MenuItem>
                  </TextField>
                  {pagamento.forma === 'Dinheiro' && (
                    <TextField
                      label="Troco para quanto?"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={pagamento.troco}
                      onChange={e => setPagamento({ ...pagamento, troco: e.target.value })}
                      style={{ marginBottom: 7 }}
                      inputRef={trocoRef}
                    />
                  )}
                  {/* Espaço extra para garantir que o último campo nunca fique coberto pelo downbar */}
                  <Box height={pedidoBarOpen ? 56 : 0} />
                </Box>
              </Box>
              <Box px={1} pb={1.2} pt={1} style={{ background: '#fff', position: 'sticky', bottom: 0, zIndex: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  style={{ fontWeight: 700, borderRadius: 8, boxShadow: '0 2px 8px #14b8a633', backgroundColor: '#14b8a6', fontSize: 16 }}
                  onClick={handleEnviarPedido}
                >
                  Enviar Pedido
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {/* Modal Cardápio do Dia */}
      <Dialog
        open={openCardapio}
        onClose={() => setOpenCardapio(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 18,
            background: '#f8fafc',
            boxShadow: '0 8px 32px #14b8a633',
            padding: isMobile ? 4 : 16,
            minWidth: isMobile ? '90vw' : 400,
            maxWidth: isMobile ? '98vw' : 480,
          }
        }}
      >
        <DialogTitle style={{
          fontWeight: 800,
          color: '#14b8a6',
          fontSize: isMobile ? 22 : 26,
          textAlign: 'center',
          letterSpacing: 1,
          paddingBottom: 0,
        }}>
          Cardápio do Dia
        </DialogTitle>
        <DialogContent style={{ padding: isMobile ? 8 : 18 }}>
          {cardapioDia ? (
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#1ea3f0', textAlign: 'center', fontSize: isMobile ? 18 : 22, marginBottom: 8 }}>
                {cardapioDia.titulo || cardapioDia.dia}
              </Typography>
              <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 2 : 4} justifyContent="center" alignItems={isMobile ? 'stretch' : 'flex-start'}>
                {cardapioDia.opcao1_nome && (
                  <Box flex={1} bgcolor="#e0f7fa" borderRadius={10} p={2} m={isMobile ? '0 0 10px 0' : '0 10px 0 0'} boxShadow="0 2px 8px #14b8a622">
                    <Typography style={{ fontWeight: 700, color: '#14b8a6', fontSize: 17, marginBottom: 4 }}>🍽️ Opção 1</Typography>
                    {cardapioDia.opcao1_nome.split(/\r?\n|,|;/).map((item, idx) => (
                      <Typography key={idx} style={{ marginLeft: 8, fontSize: 15 }}>{item.trim()}</Typography>
                    ))}
                  </Box>
                )}
                {cardapioDia.opcao2_nome && (
                  <Box flex={1} bgcolor="#e0f7fa" borderRadius={10} p={2} m={isMobile ? '0' : '0 0 0 10px'} boxShadow="0 2px 8px #14b8a622">
                    <Typography style={{ fontWeight: 700, color: '#14b8a6', fontSize: 17, marginBottom: 4 }}>🍽️ Opção 2</Typography>
                    {cardapioDia.opcao2_nome.split(/\r?\n|,|;/).map((item, idx) => (
                      <Typography key={idx} style={{ marginLeft: 8, fontSize: 15 }}>{item.trim()}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
              {cardapioDia.descricao && (
                <Box mt={2} bgcolor="#f1f5f9" borderRadius={8} p={2}>
                  {cardapioDia.descricao.split(/\r?\n|,|;/).map((item, idx) => (
                    <Typography key={idx} style={{ fontSize: 15 }}>{item.trim()}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="textSecondary" style={{ textAlign: 'center', fontSize: 16, padding: 16 }}>
              Nenhum cardápio cadastrado para hoje.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal para adicionar item */}
      <Dialog open={!!modalItem} onClose={() => setModalItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adicionar ao pedido</DialogTitle>
        <DialogContent>
          {modalItem && (
            <React.Fragment>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>{modalItem.nome}</Typography>
              <TextField
                label="Quantidade"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={modalItem.quantidade}
                onChange={e => setModalItem({ ...modalItem, quantidade: e.target.value })}
                style={{ marginBottom: 10 }}
              />
              <TextField
                label="Preço (R$)"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={modalItem.preco}
                onChange={e => setModalItem({ ...modalItem, preco: e.target.value })}
                style={{ marginBottom: 10 }}
              />
              {/* Se for marmita, mostra seleção de opção */}
              {modalItem.nome.toLowerCase().includes('marmita') && opcoesCardapioValidas.length > 0 && (
                <TextField
                  select
                  label="Opção do cardápio"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={modalItem.opcaoCardapio}
                  onChange={e => setModalItem({ ...modalItem, opcaoCardapio: e.target.value })}
                  style={{ marginBottom: 10 }}
                >
                  {cardapioDia?.opcao1_nome && (
                    <MenuItem value="1">Opção 1</MenuItem>
                  )}
                  {cardapioDia?.opcao2_nome && (
                    <MenuItem value="2">Opção 2</MenuItem>
                  )}
                </TextField>
              )}
              <TextField
                label="Observações"
                variant="outlined"
                size="small"
                fullWidth
                value={modalItem.observacoes}
                onChange={e => setModalItem({ ...modalItem, observacoes: e.target.value })}
                style={{ marginBottom: 10 }}
              />
            </React.Fragment>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalItem(null)} color="secondary">Cancelar</Button>
          <Button onClick={handleConfirmAddItem} color="primary" variant="contained">Adicionar</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </React.Fragment>
  );
}
